import { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { teacherApi, publicApi, clearTrustedDeviceToken } from "@/lib/api";
import TeacherHeader from "./TeacherHeader";
import { KeyRound, Mail, ShieldCheck, User, Smartphone, Trash2, Check, Download } from "lucide-react";
import PasswordInput from "@/components/PasswordInput";
import { useConfirm } from "@/components/ConfirmDialog";

export default function TeacherSettings() {
  const [me, setMe] = useState(null);
  const [name, setName] = useState("");
  const [newsletter, setNewsletter] = useState(false);
  const [current, setCurrent] = useState("");
  const [newPwd, setNewPwd] = useState("");
  const [confirm, setConfirm] = useState("");
  const [saving, setSaving] = useState(false);
  const [resetSent, setResetSent] = useState(false);
  const nav = useNavigate();
  const location = useLocation();

  useEffect(() => {
    // Deep-link support: /teacher/settings#danger → scroll to Danger Zone
    if (!location.hash || !me) return;
    const id = location.hash.replace("#", "");
    const el = document.getElementById(id);
    if (el) {
      // Small delay so the DOM has finished mounting nested sections
      setTimeout(() => el.scrollIntoView({ behavior: "smooth", block: "start" }), 150);
    }
  }, [location.hash, me]);

  useEffect(() => {
    teacherApi.get("/auth/me")
      .then(({ data }) => {
        setMe(data);
        setName(data.name || "");
        setNewsletter(data.preferences?.newsletter_opt_in || false);
      })
      .catch(() => nav("/teacher/login"));
  }, [nav]);

  const saveProfile = async (e) => {
    e.preventDefault();
    if (newPwd && newPwd !== confirm) { toast.error("Nowe hasła nie są identyczne"); return; }
    if (newPwd && newPwd.length < 8) { toast.error("Hasło musi mieć co najmniej 8 znaków"); return; }
    setSaving(true);
    try {
      const payload = { name, newsletter_opt_in: newsletter };
      if (newPwd) {
        payload.current_password = current;
        payload.new_password = newPwd;
      }
      await teacherApi.put("/auth/profile", payload);
      toast.success("Zapisano");
      setCurrent(""); setNewPwd(""); setConfirm("");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Błąd zapisu");
    } finally {
      setSaving(false);
    }
  };

  const sendResetLink = async () => {
    try {
      await publicApi.post("/auth/forgot-password", { email: me.email });
      setResetSent(true);
      toast.success("Wysłano link resetujący na e-mail");
    } catch {
      toast.error("Nie udało się wysłać linku");
    }
  };

  if (!me) return null;

  return (
    <div className="min-h-screen bg-[#F8FAFC]">
      <TeacherHeader />
      <main className="max-w-3xl mx-auto px-6 py-10">
        <div className="text-xs tracking-[0.2em] uppercase font-bold text-[#64748B] mb-2">Twoje konto</div>
        <h1 className="font-display font-black text-4xl mb-8">Ustawienia</h1>

        <div className="brut bg-white rounded-md p-6 mb-6" data-testid="account-info-card">
          <div className="flex items-center gap-3 mb-4">
            <div className="brut bg-[#E0E7FF] w-10 h-10 rounded-md flex items-center justify-center">
              <User className="w-5 h-5" strokeWidth={2.5} />
            </div>
            <div>
              <div className="font-display font-black text-lg">{me.name}</div>
              <div className="text-sm text-[#64748B] inline-flex items-center gap-1">
                <Mail className="w-3 h-3" strokeWidth={2.5} />{me.email}
                {me.email_verified && (
                  <span className="brut bg-[#D1FAE5] text-xs font-bold px-2 py-0.5 rounded-md ml-2 inline-flex items-center gap-1">
                    <ShieldCheck className="w-3 h-3" strokeWidth={2.5} />Zweryfikowane
                  </span>
                )}
              </div>
            </div>
            <div className="ml-auto">
              <span className="brut bg-[#FEF08A] text-xs tracking-[0.15em] uppercase font-bold px-2 py-1 rounded-md">
                {me.role === "admin" ? "Administrator" : "Nauczyciel"}
              </span>
            </div>
          </div>
        </div>

        <form onSubmit={saveProfile} className="brut bg-white rounded-md p-6 space-y-4 mb-6">
          <h2 className="font-display font-black text-xl">Profil</h2>
          <div>
            <Label className="font-bold text-sm">Nazwa wyświetlana</Label>
            <Input
              value={name} onChange={(e) => setName(e.target.value)} maxLength={80}
              className="mt-2 brut-input border-2 border-[#0F172A] rounded-md h-11"
              placeholder="Może być pseudonim — np. Chemik42"
              data-testid="settings-name-input"
            />
            <div className="text-xs text-[#64748B] mt-1.5">
              Pseudonim jest OK — pole nie musi zawierać prawdziwego imienia.
            </div>
          </div>
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox" checked={newsletter}
              onChange={(e) => setNewsletter(e.target.checked)}
              className="w-4 h-4 brut border-2 border-[#0F172A]"
              data-testid="settings-newsletter-checkbox"
            />
            <span className="text-sm">Chcę otrzymywać newsletter o nowych funkcjach</span>
          </label>

          <div className="border-t-2 border-dashed border-[#0F172A] pt-4 space-y-3">
            <h3 className="font-display font-bold text-lg inline-flex items-center gap-2">
              <KeyRound className="w-4 h-4" strokeWidth={2.5} />Zmień hasło
            </h3>
            <p className="text-xs text-[#64748B]">
              Wypełnij aby zmienić. Zostaw puste aby zostawić obecne hasło.
            </p>
            <div>
              <Label className="font-bold text-sm">Obecne hasło</Label>
              <PasswordInput
                id="settings-current-password"
                value={current} onChange={(e) => setCurrent(e.target.value)}
                className="mt-2 brut-input border-2 border-[#0F172A] rounded-md h-11 font-mono px-3"
                placeholder="••••••••" data-testid="settings-current-password"
              />
            </div>
            <div>
              <Label className="font-bold text-sm">Nowe hasło (min. 8 znaków)</Label>
              <PasswordInput
                id="settings-new-password"
                value={newPwd} onChange={(e) => setNewPwd(e.target.value)} minLength={8}
                className="mt-2 brut-input border-2 border-[#0F172A] rounded-md h-11 font-mono px-3"
                placeholder="••••••••" data-testid="settings-new-password"
              />
            </div>
            <div>
              <Label className="font-bold text-sm">Powtórz nowe hasło</Label>
              <PasswordInput
                id="settings-confirm-password"
                value={confirm} onChange={(e) => setConfirm(e.target.value)} minLength={8}
                className={`mt-2 brut-input border-2 rounded-md h-11 font-mono px-3 ${
                  confirm && newPwd !== confirm ? "border-[#EF4444]" : "border-[#0F172A]"
                }`}
                placeholder="••••••••" data-testid="settings-confirm-password"
              />
              {confirm && newPwd !== confirm && (
                <div className="text-xs text-[#EF4444] mt-1 font-bold" data-testid="settings-password-mismatch">
                  Hasła nie są identyczne
                </div>
              )}
            </div>
          </div>

          <Button
            type="submit" disabled={saving}
            className="brut brut-hover bg-[#0F172A] hover:bg-[#0F172A] text-white font-bold rounded-md h-11 px-6"
            data-testid="settings-save-btn"
          >
            {saving ? "Zapisuję..." : "Zapisz zmiany"}
          </Button>
        </form>

        <div className="brut bg-[#FEF08A] rounded-md p-6" data-testid="forgot-from-settings">
          <h3 className="font-display font-black text-lg mb-2">Nie pamiętasz aktualnego hasła?</h3>
          <p className="text-sm text-[#334155] mb-3">
            Wyślemy link resetujący na <b>{me.email}</b>. Link wygasa po 1 godzinie.
          </p>
          <Button
            onClick={sendResetLink} disabled={resetSent}
            className="brut brut-hover bg-white hover:bg-white text-[#0F172A] font-bold rounded-md border-2 border-[#0F172A]"
            data-testid="send-reset-link-btn"
          >
            <Mail className="w-4 h-4 mr-2" strokeWidth={2.5} />
            {resetSent ? "Link wysłany ✓" : "Wyślij link resetujący"}
          </Button>
        </div>

        <SecuritySection email={me.email} />
        <NotificationsSection />
        <DangerZoneSection isAdmin={me.role === "admin"} />
      </main>
    </div>
  );
}


// ============================================================================
// NOTIFICATIONS — toggle marketing categories (mirrors /unsubscribe page)
// ============================================================================
function NotificationsSection() {
  const [prefs, setPrefs] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    teacherApi.get("/auth/notification-prefs")
      .then(({ data }) => setPrefs(data))
      .catch(() => toast.error("Nie udało się pobrać ustawień powiadomień"));
  }, []);

  const toggle = async (key) => {
    if (!prefs) return;
    const next = new Set(prefs.opted_out);
    next.has(key) ? next.delete(key) : next.add(key);
    const nextArr = [...next];
    const prevArr = prefs.opted_out;
    setPrefs({ ...prefs, opted_out: nextArr });
    setSaving(true);
    try {
      const { data } = await teacherApi.put("/auth/notification-prefs",
        { opted_out: nextArr });
      setPrefs((p) => ({ ...p, opted_out: data.opted_out }));
      toast.success(next.has(key)
        ? "Wypisano — nie będziesz już otrzymywać tych maili"
        : "Zapisano — znów będziesz otrzymywać te maile");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Nie udało się zapisać");
      setPrefs((p) => ({ ...p, opted_out: prevArr }));
    } finally { setSaving(false); }
  };

  if (!prefs) return null;
  const optedSet = new Set(prefs.opted_out);

  return (
    <div className="brut bg-white rounded-md p-6 mt-8" data-testid="notifications-section">
      <h2 className="text-xl font-black uppercase mb-2">
        📬 Powiadomienia e-mail
      </h2>
      <p className="text-sm text-[#64748B] mb-5">
        Wybierz jakie e-maile chcesz otrzymywać. Wiadomości <b>transakcyjne</b>
        (weryfikacja e-mail, reset hasła, alerty bezpieczeństwa) są konieczne
        do korzystania z konta i nie można ich wyłączyć.
      </p>

      <div className="space-y-3">
        {prefs.categories.map((cat) => {
          const isOptedOut = optedSet.has(cat.key);
          return (
            <label
              key={cat.key}
              htmlFor={`notif-${cat.key}`}
              className="flex items-start gap-3 p-3 brut bg-[#FFFBEB] rounded-md cursor-pointer hover:bg-[#FEF08A] transition-colors"
              data-testid={`notif-row-${cat.key}`}
            >
              <input
                id={`notif-${cat.key}`}
                type="checkbox"
                checked={!isOptedOut}
                onChange={() => toggle(cat.key)}
                disabled={saving}
                className="mt-1 w-5 h-5 accent-[#0F172A] cursor-pointer flex-shrink-0"
                data-testid={`notif-toggle-${cat.key}`}
              />
              <div className="flex-1 min-w-0">
                <div className="font-bold text-sm">{cat.label}</div>
                <div className="text-xs text-[#64748B] mt-0.5">
                  {isOptedOut ? "Wyłączone — nie otrzymujesz tych maili"
                              : "Włączone — otrzymujesz tego typu wiadomości"}
                </div>
              </div>
            </label>
          );
        })}
      </div>
    </div>
  );
}


// ============================================================================
// DANGER ZONE — RODO/GDPR self-delete (art. 17 „prawo do bycia zapomnianym")
// ============================================================================
function DangerZoneSection({ isAdmin }) {
  const askConfirm = useConfirm();
  const nav = useNavigate();
  const [open, setOpen] = useState(false);
  const [pwd, setPwd] = useState("");
  const [phrase, setPhrase] = useState("");
  const [busy, setBusy] = useState(false);
  const [exporting, setExporting] = useState(false);

  const downloadExport = async () => {
    setExporting(true);
    try {
      const res = await teacherApi.get("/auth/export-my-data", { responseType: "blob" });
      const blob = new Blob([res.data], { type: "application/zip" });
      // Try to grab filename from Content-Disposition
      const cd = res.headers?.["content-disposition"] || "";
      const m = /filename="?([^"]+)"?/i.exec(cd);
      const filename = m ? m[1] : `kartkowki-export-${new Date().toISOString().slice(0, 10)}.zip`;
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success("Eksport gotowy — plik ZIP został pobrany");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Nie udało się pobrać eksportu");
    } finally { setExporting(false); }
  };

  if (isAdmin) {
    return (
      <div className="brut border-4 border-[#0F172A] rounded-md bg-[#F1F5F9] p-6 mt-8" data-testid="danger-zone-admin-info" id="danger">
        <h2 className="text-xl font-black uppercase mb-2">🔴 Strefa niebezpieczna</h2>
        <div className="text-sm mb-4">
          Konto administratora nie może zostać usunięte tą ścieżką. Poproś innego
          administratora o soft-delete Twojego konta w zakładce
          <b> Użytkownicy → Usuń</b>.
        </div>
        <Button
          onClick={downloadExport}
          disabled={exporting}
          className="brut brut-hover bg-white border-2 border-[#0F172A] font-bold rounded-md"
          data-testid="data-export-btn"
        >
          <Download className="w-4 h-4 mr-2" strokeWidth={2.5} />
          {exporting ? "Przygotowuję…" : "Pobierz moje dane (ZIP)"}
        </Button>
      </div>
    );
  }

  const doDelete = async () => {
    if (phrase.trim().toUpperCase() !== "USUŃ") {
      toast.error('Wpisz dokładnie: USUŃ');
      return;
    }
    if (!pwd) { toast.error("Wpisz swoje aktualne hasło"); return; }
    const ok = await askConfirm({
      title: "Ostatnie potwierdzenie",
      description: "To NIEODWRACALNA akcja. Wszystkie Twoje klasy, sesje i wyniki zostaną trwale skasowane po 30 dniach (RODO art. 17). Będziesz mieć 30 dni na kontakt z pomocą techniczną, jeśli zmienisz zdanie.",
      confirmText: "Usuń moje konto",
      variant: "danger",
    });
    if (!ok) return;
    setBusy(true);
    try {
      const { data } = await teacherApi.delete("/auth/delete-account", {
        data: { password: pwd, confirm_phrase: phrase },
      });
      toast.success(
        `Konto usunięte. Dane trwale skasujemy po ${data.retention_days} dniach. Sprawdź skrzynkę.`,
        { duration: 8000 }
      );
      // Clear all local auth state and redirect out
      try { clearTrustedDeviceToken(); } catch { /* noop */ }
      localStorage.clear();
      sessionStorage.clear();
      setTimeout(() => nav("/"), 1500);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Nie udało się usunąć konta");
    } finally { setBusy(false); }
  };

  return (
    <div
      className="brut border-4 border-[#EF4444] rounded-md bg-[#FEE2E2] p-6 mt-8 shadow-[6px_6px_0_#EF4444]"
      data-testid="danger-zone-section"
      id="danger"
    >
      <h2 className="text-xl font-black uppercase mb-2 text-[#7F1D1D]">
        🔴 Strefa niebezpieczna
      </h2>
      <p className="text-sm mb-4 text-[#7F1D1D]">
        Usunięcie konta jest <b>nieodwracalne</b>. Zgodnie z RODO (art. 17) Twoje dane
        pozostaną w systemie przez <b>30 dni</b> jako okres na ewentualne odwołanie,
        po czym zostaną trwale skasowane. Wysyłamy potwierdzenie e-mailem.
      </p>

      {/* GDPR art. 20 — data portability */}
      <div className="mb-4 pb-4 border-b-2 border-dashed border-[#EF4444]">
        <div className="text-xs font-black uppercase mb-2 text-[#7F1D1D]">
          Twoje prawa (RODO art. 20)
        </div>
        <div className="text-sm mb-3 text-[#7F1D1D]">
          Przed usunięciem — pobierz kopię wszystkich swoich danych (profil, klasy,
          sesje, wyniki, historia logowań) w formacie JSON + CSV.
        </div>
        <Button
          onClick={downloadExport}
          disabled={exporting}
          className="brut brut-hover bg-white text-[#0F172A] border-2 border-[#0F172A] font-bold rounded-md"
          data-testid="data-export-btn"
        >
          <Download className="w-4 h-4 mr-2" strokeWidth={2.5} />
          {exporting ? "Przygotowuję…" : "Pobierz moje dane (ZIP)"}
        </Button>
      </div>

      {!open ? (
        <Button
          onClick={() => setOpen(true)}
          className="brut brut-hover bg-[#EF4444] text-white border-2 border-[#0F172A] font-bold rounded-md hover:bg-[#EF4444]"
          data-testid="danger-zone-open-btn"
        >
          <Trash2 className="w-4 h-4 mr-2" strokeWidth={2.5} />
          Chcę usunąć swoje konto
        </Button>
      ) : (
        <div className="space-y-3 brut border-2 border-[#0F172A] rounded-md p-4 bg-white" data-testid="danger-zone-form">
          <div>
            <Label className="text-xs font-black uppercase">Twoje aktualne hasło</Label>
            <PasswordInput
              value={pwd}
              onChange={(e) => setPwd(e.target.value)}
              autoComplete="current-password"
              className="mt-1 brut-input border-2 border-[#0F172A] rounded-md"
              data-testid="danger-zone-password-input"
            />
          </div>
          <div>
            <Label className="text-xs font-black uppercase">
              Wpisz &bdquo;USUŃ&rdquo; aby potwierdzić
            </Label>
            <Input
              value={phrase}
              onChange={(e) => setPhrase(e.target.value)}
              placeholder='USUŃ'
              className="mt-1 brut-input border-2 border-[#0F172A] rounded-md font-mono"
              data-testid="danger-zone-phrase-input"
            />
          </div>
          <div className="flex gap-2">
            <Button
              onClick={doDelete}
              disabled={busy}
              className="brut brut-hover bg-[#EF4444] text-white border-2 border-[#0F172A] font-bold rounded-md hover:bg-[#EF4444]"
              data-testid="danger-zone-confirm-btn"
            >
              <Trash2 className="w-4 h-4 mr-2" strokeWidth={2.5} />
              {busy ? "Usuwam…" : "Usuń konto trwale"}
            </Button>
            <Button
              onClick={() => { setOpen(false); setPwd(""); setPhrase(""); }}
              disabled={busy}
              className="brut brut-hover bg-white border-2 border-[#0F172A] font-bold rounded-md"
              data-testid="danger-zone-cancel-btn"
              variant="outline"
            >
              Anuluj
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}


function SecuritySection({ email }) {
  const askConfirm = useConfirm();
  const [history, setHistory] = useState([]);
  const [twoFa, setTwoFa] = useState(false);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const [h, s] = await Promise.all([
        teacherApi.get("/auth/login-history"),
        teacherApi.get("/auth/2fa"),
      ]);
      setHistory(h.data.rows || []);
      setTwoFa(s.data.enabled || false);
    } catch {
      /* ignore */
    }
  };
  useEffect(() => { load(); }, []);

  const toggle2FA = async () => {
    setBusy(true);
    try {
      const next = !twoFa;
      await teacherApi.post("/auth/2fa", { enabled: next });
      setTwoFa(next);
      toast.success(next ? "2FA przez e-mail włączone" : "2FA wyłączone");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Błąd zmiany 2FA");
    } finally { setBusy(false); }
  };

  const logoutAll = async () => {
    const ok = await askConfirm({
      title: "Wylogować się ze wszystkich urządzeń?",
      description: "Wszystkie Twoje sesje zostaną unieważnione, a zaufane urządzenia cofnięte. Będzie trzeba zalogować się od nowa (z ponownym 2FA).",
      confirmText: "Wyloguj wszędzie",
      variant: "danger",
    });
    if (!ok) return;
    setBusy(true);
    try {
      await teacherApi.post("/auth/logout-all-devices");
      clearTrustedDeviceToken();
      toast.success("Wszystkie sesje unieważnione — zostaniesz wylogowany");
      setTimeout(() => { window.location.href = "/teacher/login"; }, 1500);
    } catch {
      toast.error("Błąd unieważniania sesji");
    } finally { setBusy(false); }
  };

  const actionLabel = (a) => ({
    "auth.login": "Logowanie",
    "auth.logout": "Wylogowanie",
    "auth.logout_all_devices": "Unieważnienie wszystkich sesji",
    "auth.otp_failed": "❌ Nieprawidłowy kod 2FA",
    "auth.password_reset_completed": "Reset hasła",
  }[a] || a);

  return (
    <div className="brut bg-white rounded-md p-6 mt-6" data-testid="security-section">
      <h2 className="font-display font-black text-xl mb-4 inline-flex items-center gap-2">
        <ShieldCheck className="w-5 h-5" strokeWidth={2.5} />Bezpieczeństwo
      </h2>

      <div className="flex items-center justify-between border-2 border-[#0F172A] rounded-md p-3 mb-3">
        <div>
          <div className="font-bold text-sm">Logowanie dwuetapowe (2FA przez e-mail)</div>
          <div className="text-xs text-[#64748B]">
            Po podaniu hasła wysyłamy 6-cyfrowy kod na <b>{email}</b>. Kod ważny 5 min.
          </div>
        </div>
        <Button
          onClick={toggle2FA} disabled={busy}
          className={`brut brut-hover font-bold rounded-md border-2 border-[#0F172A] ${twoFa ? "bg-[#D1FAE5]" : "bg-white"}`}
          data-testid="toggle-2fa-btn"
        >
          {twoFa ? "Włączone ✓ — Wyłącz" : "Włącz 2FA"}
        </Button>
      </div>

      <TrustedDevicesSection />

      <div className="flex items-center justify-between border-2 border-[#0F172A] rounded-md p-3 mb-4">
        <div>
          <div className="font-bold text-sm">Wyloguj wszystkie urządzenia</div>
          <div className="text-xs text-[#64748B]">
            Unieważnia tokeny na wszystkich urządzeniach (przeglądarki, telefony) <b>oraz cofa wszystkie zaufane urządzenia</b>. Przydatne gdy podejrzewasz, że ktoś inny ma dostęp.
          </div>
        </div>
        <Button
          onClick={logoutAll} disabled={busy}
          className="brut brut-hover bg-[#FECACA] text-[#0F172A] font-bold rounded-md border-2 border-[#0F172A]"
          data-testid="logout-all-btn"
        >
          Wyloguj wszędzie
        </Button>
      </div>

      <h3 className="font-bold text-sm mb-2 mt-4">Ostatnie zdarzenia bezpieczeństwa</h3>
      <div className="space-y-1 max-h-[300px] overflow-y-auto" data-testid="login-history">
        {history.length === 0 && <div className="text-sm text-[#64748B]">Brak zdarzeń.</div>}
        {history.map((r, i) => (
          <div key={i} className="brut border-2 border-[#0F172A] rounded-md p-2 bg-white text-xs" data-testid={`login-history-row-${i}`}>
            <div className="flex justify-between flex-wrap gap-2">
              <span className="font-bold">{actionLabel(r.action)}</span>
              <span className="text-[#64748B]">{new Date(r.ts).toLocaleString("pl")}</span>
            </div>
            {r.metadata?.ip && (
              <div className="text-[#475569] font-mono mt-1">
                IP: {r.metadata.ip}
                {r.metadata.two_factor && <span className="ml-2 text-[#10B981]">· 2FA ✓</span>}
                {r.metadata.user_agent && <span className="ml-2 text-[#94A3B8]">· {r.metadata.user_agent.slice(0, 60)}</span>}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}


function TrustedDevicesSection() {
  const askConfirm = useConfirm();
  const [rows, setRows] = useState([]);
  const [ttlDays, setTtlDays] = useState(60);
  const [busy, setBusy] = useState(false);
  const [loaded, setLoaded] = useState(false);

  const load = async () => {
    try {
      const { data } = await teacherApi.get("/auth/trusted-devices");
      setRows(data.rows || []);
      if (data.ttl_days) setTtlDays(data.ttl_days);
    } catch (e) {
      console.warn("trusted-devices load failed", e?.response?.status || e);
    } finally {
      setLoaded(true);
    }
  };
  useEffect(() => { load(); }, []);

  const revokeOne = async (device) => {
    const ok = await askConfirm({
      title: "Cofnąć zaufanie urządzenia?",
      description: `Urządzenie „${shortUa(device.user_agent)}" straci status zaufanego. Przy następnym logowaniu z niego będzie wymagane ponowne 2FA.`,
      confirmText: "Cofnij zaufanie",
      variant: "danger",
    });
    if (!ok) return;
    setBusy(true);
    try {
      await teacherApi.delete(`/auth/trusted-devices/${device.id}`);
      toast.success("Urządzenie cofnięte — wymaga ponownego 2FA");
      if (device.is_current) clearTrustedDeviceToken();
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Błąd cofania urządzenia");
    } finally { setBusy(false); }
  };

  const revokeAll = async () => {
    if (!rows.length) return;
    const ok = await askConfirm({
      title: "Cofnąć wszystkie zaufane urządzenia?",
      description: `${rows.length} ${rows.length === 1 ? "urządzenie utraci" : "urządzeń utraci"} status zaufanego. Przy następnym logowaniu z każdego z nich będzie wymagane ponowne 2FA.`,
      confirmText: "Cofnij wszystkie",
      variant: "danger",
    });
    if (!ok) return;
    setBusy(true);
    try {
      await teacherApi.delete("/auth/trusted-devices");
      clearTrustedDeviceToken();
      toast.success("Wszystkie urządzenia cofnięte");
      await load();
    } catch {
      toast.error("Błąd cofania urządzeń");
    } finally { setBusy(false); }
  };

  return (
    <div className="border-2 border-[#0F172A] rounded-md p-3 mb-3" data-testid="trusted-devices-section">
      <div className="flex items-start justify-between gap-3 mb-3">
        <div>
          <div className="font-bold text-sm inline-flex items-center gap-2">
            <Smartphone className="w-4 h-4" strokeWidth={2.5} />Zaufane urządzenia
          </div>
          <div className="text-xs text-[#64748B] mt-0.5">
            Urządzenia, które pomijają kod 2FA. Ważne {ttlDays} dni od ostatniego użycia.
          </div>
        </div>
        {rows.length > 1 && (
          <Button
            onClick={revokeAll} disabled={busy}
            className="brut brut-hover bg-white text-[#0F172A] font-bold rounded-md border-2 border-[#0F172A] h-8 text-xs"
            data-testid="revoke-all-trusted-devices-btn"
          >
            Cofnij wszystkie
          </Button>
        )}
      </div>

      {!loaded ? (
        <div className="text-xs text-[#94A3B8] italic" data-testid="trusted-devices-loading">
          Wczytywanie urządzeń…
        </div>
      ) : rows.length === 0 ? (
        <div className="text-xs text-[#64748B] italic" data-testid="no-trusted-devices">
          Brak zaufanych urządzeń. Włącz „Zaufaj temu urządzeniu&rdquo; podczas logowania z 2FA.
        </div>
      ) : (
        <div className="space-y-2" data-testid="trusted-devices-list">
          {rows.map((d) => (
            <div key={d.id}
                 className="brut border-2 border-[#0F172A] rounded-md p-2 bg-white text-xs flex items-start justify-between gap-2"
                 data-testid={`trusted-device-row-${d.id}`}>
              <div className="flex-1 min-w-0">
                <div className="font-bold flex items-center gap-2 flex-wrap">
                  {shortUa(d.user_agent)}
                  {d.is_current && (
                    <span className="brut bg-[#D1FAE5] text-[10px] font-bold px-2 py-0.5 rounded-md inline-flex items-center gap-1">
                      <Check className="w-3 h-3" strokeWidth={3} />Bieżące
                    </span>
                  )}
                </div>
                <div className="text-[#475569] font-mono mt-1 break-all">
                  {fmtGeo(d.geo, d.ip)} · Dodane: {fmtDate(d.created_at)} · Wygasa: {fmtDate(d.expires_at)}
                </div>
                {d.last_used_at && (
                  <div className="text-[#94A3B8] mt-0.5">Ostatnio użyte: {fmtDate(d.last_used_at)}</div>
                )}
              </div>
              <Button
                onClick={() => revokeOne(d)} disabled={busy}
                className="brut brut-hover bg-[#FECACA] text-[#0F172A] font-bold rounded-md border-2 border-[#0F172A] h-8 px-2 text-xs"
                data-testid={`revoke-trusted-device-${d.id}`}
              >
                <Trash2 className="w-3 h-3" strokeWidth={2.5} />
              </Button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function shortUa(ua) {
  if (!ua) return "Nieznane urządzenie";
  // Quick heuristic — pull the major browser / OS hints
  const m = ua.match(/(Chrome|Firefox|Safari|Edg|Opera)\/[\d.]+/);
  const osMatch = ua.match(/\((.*?)\)/);
  const os = osMatch ? osMatch[1].split(";").slice(0, 2).join(",").trim() : "";
  return [m ? m[0].split("/")[0] : "Przeglądarka", os].filter(Boolean).join(" · ").slice(0, 80) || "Urządzenie";
}

function fmtDate(iso) {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleString("pl"); } catch { return iso; }
}

function fmtGeo(geo, ip) {
  if (!geo) return `IP: ${ip || "?"}`;
  const parts = [];
  if (geo.city) parts.push(geo.city);
  if (geo.country_code) parts.push(geo.country_code);
  else if (geo.country) parts.push(geo.country);
  const label = parts.length ? parts.join(", ") : (ip || "?");
  return `${label} (${ip || "?"})`;
}
